import { create } from 'zustand';
import type { PerkId, RunPhase, RunState, RunActiveBuff, Stats } from '../game/types';
import { createRng, shuffle } from '../game/systems/rng';
import { generateRunLayout } from '../game/systems/procgen';
import { applyPerk, choosePerkOffers, PERK_POOL } from '../game/systems/perks';
import { addXp as addXpReducer } from '../game/systems/xp';
import { ZERO_EFFECTS, DEFAULT_CAT_CLASS_ID, type HideoutEffects, type CatClassId, type ConsumableId, type BossId, type ChallengeModifierId, type SectorMod } from '@cradle7/shared';
import { useHideoutStore } from '../hideoutStore';
import { useLoadoutStore } from '../loadoutStore';
import { useConsumablesStore } from '../consumablesStore';
import { getCatClass } from '../catClasses';
import { getUserSettings } from '../userSettings';
import { useCatStore } from '../catStore';
import { applyScarMutationEffects, computeBonusRevives, computePerkPickerDelta } from '../game/systems/scars';
import { forcedClassForChallenge } from './challengeMode';
import { computeEvolutions, applyEvolution, type EvolutionId } from '../game/systems/evolutions';
import { EVT, gameEvents } from '../game/events';

const STARTING_STATS: Stats = {
  maxHp: 100,
  hp: 100,
  damageMultiplier: 1,
  moveSpeed: 220,
  fireRate: 1.5,
};

const STARTING_ROOM_COUNT = 6;

/** Per-run UUID for telemetry correlation (API validates z.string().uuid()).
 *  crypto.randomUUID() exists only in secure contexts (https/localhost) —
 *  on-device LAN testing (http://<LAN-IP>:5173) and old WebViews (Android
 *  Chromium <92, iOS <15.4) would throw inside startRun and soft-brick the
 *  ▶ button. getRandomValues is NOT secure-context-gated, so fall back to a
 *  manual RFC 4122 v4. */
function newRunUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
    ? crypto.getRandomValues(new Uint8Array(16))
    : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant 10xx
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

type Actions = {
  startRun: (seed?: number, effects?: HideoutEffects, catClassId?: CatClassId, loadoutPerk?: PerkId | null) => void;
  setPhase: (phase: RunPhase) => void;
  advanceRoom: () => void;
  rollPerkOffer: () => void;
  rerollPerkOffer: () => void;
  takePerk: (id: PerkId) => void;
  /** Phase 2u: record one perk-choice event where the player rerolled/skipped
   *  the offered set. Appends to offeredSkippedLog; included in submitRun. */
  appendOfferedSkipped: (skipped: PerkId[]) => void;
  addLootAtRisk: (n: number) => void;
  secureAtRiskLoot: () => void;
  loseAtRiskLoot: () => void;
  recordKill: () => void;
  damageCat: (amount: number) => void;
  healCat: (amount: number) => void;
  addPickedConsumable: (id: ConsumableId) => void;
  useConsumableSlot: (index: number, newState: Partial<RunState>) => void;
  setActiveBuffs: (buffs: RunActiveBuff[]) => void;
  recordBossDefeated: (bossId: BossId) => void;
  setReactorMode: (on: boolean) => void;
  dismissReactorPrompt: () => void;
  setChallengeMode: (c: { date: string; modifierId: ChallengeModifierId } | null) => void;
  startChallengeRun: (challenge: { date: string; modifierId: ChallengeModifierId; seed: number }) => void;
  /** Phase 3h: set the sector modifier active for this run. Called by
   *  SectorPreviewModal.deploy() right before startRun(). null clears. */
  setSectorMod: (mod: SectorMod | null) => void;
  /** Phase 3h: storm cycle toggle. Driven by RunScene's storm ticker. */
  setStormActive: (active: boolean) => void;
  /** Survivor: grant XP from a collected gem; queues level-ups as the curve crosses. */
  addXp: (amount: number) => void;
  /** Survivor: open one queued level-up offer (enters level_up phase) or skip if pool exhausted. */
  consumeLevelUp: () => void;
  /** Survivor: (re)start a sector survival timer with the given whole-second duration. */
  startSector: (durationSec: number) => void;
  /** Survivor: push the remaining whole-second count for the HUD. */
  setSectorTimeLeft: (sec: number) => void;
  resetToMenu: () => void;
};

const INITIAL: RunState = {
  phase: 'menu',
  seed: 0,
  clientRunId: '',
  runStartedAt: 0,
  layout: null,
  currentRoomIndex: 0,
  catStats: { ...STARTING_STATS },
  ownedPerks: [],
  pendingPerkOffer: null,
  lootCount: 0,
  lootAtRisk: 0,
  enemiesKilled: 0,
  revivesRemaining: 0,
  rerollsRemaining: 0,
  perkPickerCount: 3,
  selectedCatClassId: DEFAULT_CAT_CLASS_ID,
  firstHitConsumed: false,
  shieldCharges: 0,
  shieldIframesUntil: 0,
  moveSpeedMul: 1,
  activeBuffs: [],
  consumables: [null, null, null],
  consumablesUsed: [],
  consumablesPicked: [],
  bossDefeated: null,
  reactorMode: false,
  reactorPromptDismissed: false,
  challengeMode: null,
  appliedEvolutions: new Set<EvolutionId>(),
  offeredSkippedLog: [],
  sectorMod: null,
  stormActive: false,
  level: 1,
  xp: 0,
  pendingLevelUps: 0,
  sectorDurationSec: 0,
  sectorTimeLeftSec: 0,
};

export const useRunStore = create<RunState & Actions>((set, get) => ({
  ...INITIAL,

  startRun: (seed, effects?: HideoutEffects, catClassId?: CatClassId, loadoutPerk?: PerkId | null) => {
    // Phase 2h: callers (Home reactor prompt) set reactorMode/dismissed BEFORE startRun.
    // Spreading INITIAL below would clobber these, so capture + re-apply.
    const reactorMode = get().reactorMode;
    const reactorPromptDismissed = get().reactorPromptDismissed;
    // Phase 3h: SectorPreviewModal.deploy() sets sectorMod BEFORE startRun;
    // same capture-and-reapply pattern as reactorMode so RunScene.create()
    // can read it via useRunStore.getState().sectorMod. (Caught by opus —
    // without this the entire 3h biohazard/storm chain was a no-op.)
    const sectorMod = get().sectorMod;
    const resolvedEffects = effects ?? useHideoutStore.getState().effects;
    const resolvedClass = catClassId ?? getUserSettings().selectedCatClassId;
    // explicit arg beats store; arg === undefined → fallback to store
    const resolvedLoadout = loadoutPerk !== undefined
      ? loadoutPerk
      : useLoadoutStore.getState().loadoutPerkId;
    const klass = getCatClass(resolvedClass);

    const s = seed ?? Math.floor(Math.random() * 1e9);
    const clientRunId = newRunUuid();
    const rng = createRng(s);

    // Step 1: baseline × class multipliers.
    let stats: Stats = {
      maxHp: STARTING_STATS.maxHp * klass.stats.maxHpMul,
      hp: STARTING_STATS.maxHp * klass.stats.maxHpMul,
      damageMultiplier: STARTING_STATS.damageMultiplier * klass.stats.damageMul,
      moveSpeed: STARTING_STATS.moveSpeed * klass.stats.moveSpeedMul,
      fireRate: STARTING_STATS.fireRate * klass.stats.fireRateMul,
    };

    // Step 2: class starting perks (deterministic order).
    const owned: PerkId[] = [];
    for (const perkId of klass.startingPerks) {
      stats = applyPerk(stats, perkId);
      owned.push(perkId);
    }

    // Step 3 (Phase 2e): loadout perk if set and not already in owned.
    if (resolvedLoadout && !owned.includes(resolvedLoadout)) {
      stats = applyPerk(stats, resolvedLoadout);
      owned.push(resolvedLoadout);
    }

    // Phase 2f: scar/mutation effects from active cat
    const activeCat = useCatStore.getState().active[resolvedClass];
    const scars = activeCat?.scars ?? [];
    const mutations = activeCat?.mutations ?? [];

    stats = applyScarMutationEffects(stats, scars, mutations);
    const bonusRevives = computeBonusRevives(mutations);
    const perkPickerDelta = computePerkPickerDelta(scars, mutations);

    // Step 4: Cryo hpBonus — additive to maxHp + hp.
    if (resolvedEffects.hpBonus > 0) {
      stats = {
        ...stats,
        maxHp: stats.maxHp + resolvedEffects.hpBonus,
        hp: stats.hp + resolvedEffects.hpBonus,
      };
    }

    // Step 5: Gym damageMul — multiplicative on top of class damageMul.
    if (resolvedEffects.damageMul !== 1) {
      stats = { ...stats, damageMultiplier: stats.damageMultiplier * resolvedEffects.damageMul };
    }

    // Step 6: Hangar pre-selected perks (exclude already-owned class + loadout perks).
    if (resolvedEffects.preSelectedPerks > 0) {
      const preselectRng = createRng(s + 4242);
      const eligible = PERK_POOL.filter((p) => !owned.includes(p.id));
      const shuffled = shuffle(preselectRng, eligible);
      const picks = shuffled.slice(0, Math.min(resolvedEffects.preSelectedPerks, shuffled.length));
      for (const p of picks) {
        stats = applyPerk(stats, p.id);
        owned.push(p.id);
      }
    }

    // Phase 2g — snapshot consumables loadout at run start
    const consumablesLoadout = useConsumablesStore.getState().loadout;
    const consumables: RunState['consumables'] = [
      consumablesLoadout[0],
      consumablesLoadout[1],
      consumablesLoadout[2],
    ];

    set({
      ...INITIAL,
      seed: s,
      clientRunId,
      runStartedAt: Date.now(),
      layout: generateRunLayout({ rng, roomCount: reactorMode ? 4 : STARTING_ROOM_COUNT }),
      phase: 'in_combat',
      catStats: stats,
      ownedPerks: owned,
      selectedCatClassId: resolvedClass,
      revivesRemaining: resolvedEffects.extraRevives + bonusRevives,
      rerollsRemaining: resolvedEffects.rerolls,
      perkPickerCount: Math.max(1, 3 + resolvedEffects.perkPickerBonus + perkPickerDelta),
      firstHitConsumed: false,
      activeBuffs: [],
      shieldIframesUntil: 0,
      moveSpeedMul: 1,
      consumables,
      consumablesUsed: [],
      consumablesPicked: [],
      reactorMode,
      reactorPromptDismissed,
      // Phase 3h: keep the deploy-time sector mod so RunScene.create() can wire
      // biohazard tick / storm cycle. stormActive deliberately starts false —
      // RunScene update() pushes the live value once the storm cycle ticks.
      sectorMod,
      stormActive: false,
      // Phase 2i: a regular startRun is NEVER a challenge run. Always wipe.
      challengeMode: null,
    });
  },

  setPhase: (phase) => set({ phase }),

  advanceRoom: () => {
    const { layout, currentRoomIndex } = get();
    if (!layout) return;
    const next = currentRoomIndex + 1;
    if (next >= layout.rooms.length) {
      set({ phase: 'won' });
      return;
    }
    const nextRoom = layout.rooms[next]!;
    // Phase-0 simplification: 'loot' and 'event' room types render as combat
    // rooms (with no special mechanics). Distinct phases land in Phase 1.
    set({
      currentRoomIndex: next,
      phase: nextRoom.type === 'extraction' ? 'extracting' : 'in_combat',
    });
  },

  rollPerkOffer: () => {
    const { seed, currentRoomIndex, ownedPerks, perkPickerCount } = get();
    // Guard against pool exhaustion: if no perks remain eligible, advance to
    // the next room (which spawns the next wave) instead of just flipping the
    // phase back to in_combat — that left the player stuck in the just-cleared
    // room with no enemies and no perk prompt (soft-lock).
    if (PERK_POOL.length - ownedPerks.length < 1) {
      set({ pendingPerkOffer: null });
      get().advanceRoom();
      return;
    }
    const offerRng = createRng(seed + currentRoomIndex * 7919);
    const offers = choosePerkOffers({
      rng: offerRng,
      excludeIds: new Set(ownedPerks),
      count: perkPickerCount,
    });
    set({
      pendingPerkOffer: offers.map((p) => p.id),
      phase: 'perk_choice',
    });
  },

  rerollPerkOffer: () => {
    const { seed, currentRoomIndex, ownedPerks, perkPickerCount, rerollsRemaining } = get();
    if (rerollsRemaining <= 0) return;
    if (PERK_POOL.length - ownedPerks.length < 1) return;
    const offerRng = createRng(seed + currentRoomIndex * 7919 + rerollsRemaining * 31);
    const offers = choosePerkOffers({
      rng: offerRng,
      excludeIds: new Set(ownedPerks),
      count: perkPickerCount,
    });
    set({ pendingPerkOffer: offers.map((p) => p.id), rerollsRemaining: rerollsRemaining - 1 });
  },

  takePerk: (id) => {
    const { catStats: prevStats, ownedPerks, appliedEvolutions } = get();
    const valid = PERK_POOL.some((p) => p.id === id);
    if (!valid) throw new Error(`takePerk: unknown perk id ${id}`);
    let catStats = applyPerk(prevStats, id);
    const ownedPerksNext = [...ownedPerks, id];

    // Phase 2t: detect newly-unlocked evolutions and apply their stat bonuses.
    const ownedSet = new Set<PerkId>(ownedPerksNext);
    const active = computeEvolutions(ownedSet);
    const newlyUnlocked = active.filter((e) => !appliedEvolutions.has(e));
    for (const evo of newlyUnlocked) {
      catStats = applyEvolution(catStats, evo);
      gameEvents.emit(EVT.EvolutionUnlocked, { id: evo });
    }
    const appliedEvolutionsNext: ReadonlySet<EvolutionId> =
      newlyUnlocked.length === 0
        ? appliedEvolutions
        : new Set<EvolutionId>([...appliedEvolutions, ...newlyUnlocked]);

    set({
      catStats,
      ownedPerks: ownedPerksNext,
      pendingPerkOffer: null,
      phase: 'in_combat',
      appliedEvolutions: appliedEvolutionsNext,
    });
  },

  appendOfferedSkipped: (skipped) => {
    set((s) => ({ ...s, offeredSkippedLog: [...s.offeredSkippedLog, skipped] }));
  },

  addLootAtRisk: (n) => set((s) => ({ lootAtRisk: s.lootAtRisk + n })),
  secureAtRiskLoot: () => set((s) => ({
    lootCount: s.lootCount + s.lootAtRisk,
    lootAtRisk: 0,
  })),
  loseAtRiskLoot: () => set({ lootAtRisk: 0 }),

  recordKill: () => set((s) => ({ enemiesKilled: s.enemiesKilled + 1 })),

  damageCat: (amount) => {
    const { catStats, revivesRemaining } = get();
    const hp = Math.max(0, catStats.hp - amount);
    if (hp <= 0 && revivesRemaining > 0) {
      // Auto-revive: restore to 50% HP and decrement counter.
      set({
        catStats: { ...catStats, hp: Math.floor(catStats.maxHp / 2) },
        revivesRemaining: revivesRemaining - 1,
      });
      return;
    }
    set({ catStats: { ...catStats, hp } });
    if (hp <= 0) set({ phase: 'lost', lootAtRisk: 0 }); // lose at-risk loot on death
  },

  healCat: (amount) => {
    const { catStats } = get();
    const hp = Math.min(catStats.maxHp, catStats.hp + amount);
    set({ catStats: { ...catStats, hp } });
  },

  addPickedConsumable: (id) => set((s) => ({ consumablesPicked: [...s.consumablesPicked, id] })),

  useConsumableSlot: (index, newState) => set((s) => {
    const consumed = s.consumables[index];
    if (!consumed) return {};
    const consumables = [...s.consumables] as RunState['consumables'];
    consumables[index] = null;
    return { ...newState, consumables, consumablesUsed: [...s.consumablesUsed, consumed] };
  }),

  setActiveBuffs: (buffs) => set({ activeBuffs: buffs }),

  recordBossDefeated: (bossId) => set({ bossDefeated: bossId }),

  setReactorMode: (on) => set({ reactorMode: on }),
  setSectorMod: (mod) => set({ sectorMod: mod }),
  setStormActive: (active) => set({ stormActive: active }),

  addXp: (amount) => set((s) => {
    const next = addXpReducer({ level: s.level, xp: s.xp }, amount);
    return {
      level: next.level,
      xp: next.xp,
      pendingLevelUps: s.pendingLevelUps + next.levelsGained,
    };
  }),

  consumeLevelUp: () => {
    const { pendingLevelUps, ownedPerks, perkPickerCount, seed, level } = get();
    if (pendingLevelUps <= 0) return;
    // Pool exhausted → consume the queued level-up without opening a modal.
    if (PERK_POOL.length - ownedPerks.length < 1) {
      set({ pendingLevelUps: pendingLevelUps - 1 });
      return;
    }
    const offerRng = createRng(seed + level * 6151 + pendingLevelUps * 17);
    const offers = choosePerkOffers({
      rng: offerRng,
      excludeIds: new Set(ownedPerks),
      count: perkPickerCount,
    });
    set({
      pendingPerkOffer: offers.map((p) => p.id),
      phase: 'level_up',
      pendingLevelUps: pendingLevelUps - 1,
    });
  },

  startSector: (durationSec) => set({ sectorDurationSec: durationSec, sectorTimeLeftSec: durationSec }),
  setSectorTimeLeft: (sec) => set({ sectorTimeLeftSec: sec }),

  dismissReactorPrompt: () => set({ reactorPromptDismissed: true }),

  setChallengeMode: (c) => set({ challengeMode: c }),

  // Phase 2i: start a daily-challenge run. Mutually exclusive with reactorMode
  // (we force-clear it). For class_lock_* modifiers we override the player's
  // chosen class via forcedClassForChallenge.
  startChallengeRun: (challenge) => {
    const forcedClass = forcedClassForChallenge(challenge.modifierId);
    // Ensure reactorMode is OFF before startRun captures it for the spread.
    set({ reactorMode: false });
    // Reuse startRun's stat/layout/perk pipeline (forcedClass when locked).
    get().startRun(challenge.seed, undefined, forcedClass ?? undefined);
    // startRun explicitly clears challengeMode → re-apply after the fact.
    set({ challengeMode: { date: challenge.date, modifierId: challenge.modifierId } });
  },

  // resetToMenu wipes the run but preserves reactorPromptDismissed so the
  // player isn't pestered after they tapped ОТЛОЖИТЬ. reactorMode is reset
  // (back to false) — a fresh ▶ launch should never be reactor-mode by accident.
  resetToMenu: () => set((s) => ({ ...INITIAL, reactorPromptDismissed: s.reactorPromptDismissed })),
}));
